import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Form,
  Input,
  message,
  Modal,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';

import { platformSubscriptionChangeRequestsService } from '../../services/api';
import { usePlatformActionPermissions } from '../../hooks/usePlatformActionPermissions';
import {
  buildPlanTransitionLabel,
  formatDateOnlyPtBR,
  getScheduledCancelLifecycleLabels,
  isCancelNoopTerminal,
  isScheduledCancelProcessed,
  isScheduledCancelRequest,
  isScheduledCancelRevoked,
  isScheduledDowngradeRequest,
  matchesAdminScopeFilter,
  resolveScheduledDowngradeSummary,
} from './PlatformSubscriptionRequestsPage.utils';

const { Title, Text } = Typography;

const STATUS_OPTIONS = [
  { label: 'PENDING', value: 'PENDING' },
  { label: 'APPROVED', value: 'APPROVED' },
  { label: 'REJECTED', value: 'REJECTED' },
  { label: 'CANCELLED', value: 'CANCELLED' },
];

const TYPE_OPTIONS = [
  { label: 'UPGRADE', value: 'UPGRADE' },
  { label: 'DOWNGRADE', value: 'DOWNGRADE' },
  { label: 'CANCEL', value: 'CANCEL' },
];

const SCOPE_OPTIONS = [
  { label: 'Todos', value: 'all' },
  { label: 'Somente downgrades agendados', value: 'scheduled_downgrade' },
  { label: 'Somente cancelamentos agendados', value: 'scheduled_cancel' },
];

const STATUS_COLOR = {
  PENDING: 'gold',
  APPROVED: 'green',
  REJECTED: 'red',
  CANCELLED: 'default',
};

const FULFILLMENT_COLOR = {
  NOT_STARTED: 'default',
  PAYMENT_IN_PROGRESS: 'processing',
  PAID_PENDING_APPLY: 'gold',
  EFFECTUATED: 'green',
  FAILED: 'red',
  NOOP_TERMINAL: 'default',
};

const FULFILLMENT_LABEL = {
  NOT_STARTED: 'Nao iniciado',
  PAYMENT_IN_PROGRESS: 'Pagamento em andamento',
  PAID_PENDING_APPLY: 'Pago aguardando aplicacao',
  EFFECTUATED: 'Efetivado',
  FAILED: 'Falhou',
  NOOP_TERMINAL: 'No-op terminal',
};

const PAYMENT_STATE_COLOR = {
  not_started: 'default',
  pending_provider_setup: 'processing',
  pending_payment: 'gold',
  paid: 'green',
  overdue: 'red',
  payment_setup_failed: 'red',
  canceled: 'default',
};

const PAYMENT_STATE_LABEL = {
  not_started: 'Nao iniciado',
  pending_provider_setup: 'Cobranca em preparo',
  pending_payment: 'Aguardando pagamento',
  paid: 'Pagamento confirmado',
  overdue: 'Pagamento em atraso',
  payment_setup_failed: 'Falha ao preparar cobranca',
  canceled: 'Cobranca cancelada',
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleString('pt-BR');
};

const getApiError = (requestError, fallback) => {
  const detail = requestError?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  return fallback;
};

const PERMISSION_TOOLTIP_MESSAGE = 'Você não possui permissão para executar esta ação';

const withPermissionTooltip = (node, disabledByPermission) => {
  if (!disabledByPermission) {
    return node;
  }

  return (
    <Tooltip title={PERMISSION_TOOLTIP_MESSAGE}>
      <span style={{ display: 'inline-block' }}>{node}</span>
    </Tooltip>
  );
};

const renderPaymentStateTag = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '-';
  return <Tag color={PAYMENT_STATE_COLOR[normalized] || 'default'}>{PAYMENT_STATE_LABEL[normalized] || normalized}</Tag>;
};

const renderExecutionMode = (value) => {
  if (!value) return '-';
  const color = value === 'manual_only' ? 'default' : 'processing';
  return <Tag color={color}>{value}</Tag>;
};

const normalizeFulfillment = (value) => {
  const normalized = String(value || '').trim().toUpperCase();
  if (!normalized || !Object.prototype.hasOwnProperty.call(FULFILLMENT_LABEL, normalized)) {
    return null;
  }
  return normalized;
};

const renderFulfillmentTag = (value) => {
  const normalized = normalizeFulfillment(value);
  if (!normalized) {
    return <Tag color="default">LEGACY_FALLBACK</Tag>;
  }
  return <Tag color={FULFILLMENT_COLOR[normalized] || 'default'}>{FULFILLMENT_LABEL[normalized] || normalized}</Tag>;
};

const resolveLegacyOperationalWarning = (row) => {
  const paymentState = String(row?.payment_state || '').trim().toLowerCase();
  const executionMode = String(row?.execution_mode || '').trim();

  if (paymentState === 'paid') return 'Pagamento confirmado';
  if (paymentState === 'payment_setup_failed') return 'Falha ao preparar cobranca';
  if (paymentState === 'pending_payment') return 'Aguardando pagamento';
  if (executionMode === 'manual_only' || executionMode === 'auto_apply_local_fallback') {
    return 'Cobranca ainda nao gerada automaticamente';
  }
  return '-';
};

const getOperationalWarning = (row) => {
  if (isScheduledDowngradeRequest(row)) {
    return resolveScheduledDowngradeSummary(row);
  }

  if (isScheduledCancelRequest(row)) {
    return 'Cancelamento agendado para data futura; assinatura permanece ativa ate a data efetiva.';
  }
  if (isScheduledCancelRevoked(row)) {
    return 'Cancelamento agendado foi revogado antes da efetivacao.';
  }
  if (isScheduledCancelProcessed(row)) {
    return `Cancelamento processado em ${formatDateTime(row?.scheduled_processed_at)}.`;
  }
  if (isCancelNoopTerminal(row)) {
    return `No-op terminal${row?.noop_reason ? `: ${row.noop_reason}` : ''}`;
  }

  const fulfillment = normalizeFulfillment(row?.fulfillment);
  if (fulfillment === 'PAYMENT_IN_PROGRESS') {
    return 'Aprovado e em execucao financeira (PAYMENT_IN_PROGRESS).';
  }
  if (fulfillment === 'PAID_PENDING_APPLY') {
    return 'Pagamento confirmado aguardando aplicacao do upgrade (PAID_PENDING_APPLY).';
  }
  if (fulfillment === 'EFFECTUATED') {
    return 'Upgrade efetivado (EFFECTUATED).';
  }
  if (fulfillment === 'FAILED') {
    return `Falha operacional (FAILED)${row?.reason_code ? `: ${row.reason_code}` : ''}`;
  }
  if (fulfillment === 'NOOP_TERMINAL') {
    return `Fluxo encerrado sem aplicacao (NOOP_TERMINAL)${row?.noop_reason ? `: ${row.noop_reason}` : ''}`;
  }
  if (fulfillment === 'NOT_STARTED') {
    return row?.status === 'PENDING'
      ? 'Solicitacao aguardando aprovacao comercial.'
      : 'Aprovado comercialmente, aguardando inicio operacional.';
  }
  const legacyWarning = resolveLegacyOperationalWarning(row);
  if (!legacyWarning || legacyWarning === '-') return 'Sem sinal operacional relevante.';
  return `${legacyWarning} (fallback legado)`;
};

const PlatformSubscriptionRequestsPage = () => {
  const { canExecuteAction } = usePlatformActionPermissions();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [prepareSummary, setPrepareSummary] = useState(null);
  const [prepareLoadingById, setPrepareLoadingById] = useState({});
  const [items, setItems] = useState([]);
  const [filters, setFilters] = useState({});
  const [decisionModal, setDecisionModal] = useState({ open: false, mode: null, request: null });
  const [form] = Form.useForm();

  const loadItems = useCallback(async (customFilters = filters) => {
    setLoading(true);
    setError('');
    try {
      const apiFilters = { ...(customFilters || {}) };
      delete apiFilters.scope;

      const response = await platformSubscriptionChangeRequestsService.list(apiFilters);
      const rows = Array.isArray(response?.data) ? response.data : [];
      const filteredRows = rows.filter((row) => matchesAdminScopeFilter(row, customFilters?.scope));
      setItems(filteredRows);
    } catch (requestError) {
      setError(getApiError(requestError, 'Nao foi possivel carregar as solicitacoes.'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const applyFilters = async (values) => {
    const payload = {
      status: values.status || undefined,
      tenant_id: values.tenant_id || undefined,
      request_type: values.request_type || undefined,
      plan_code: values.plan_code || undefined,
      created_from: values.created_range?.[0] ? values.created_range[0].startOf('day').toISOString() : undefined,
      created_to: values.created_range?.[1] ? values.created_range[1].endOf('day').toISOString() : undefined,
      scope: values.scope || 'all',
    };
    setFilters(payload);
    await loadItems(payload);
  };

  const openDecision = useCallback((mode, requestItem) => {
    setDecisionModal({ open: true, mode, request: requestItem });
    form.resetFields();
  }, [form]);

  const closeDecision = () => {
    setDecisionModal({ open: false, mode: null, request: null });
    form.resetFields();
  };

  const submitDecision = async (values) => {
    if (!decisionModal.request) return;

    setActionLoading(true);
    setError('');
    setSuccessMessage('');

    const payload = { internal_notes: values.internal_notes || null };

    try {
      if (decisionModal.mode === 'approve') {
        await platformSubscriptionChangeRequestsService.approve(decisionModal.request.id, payload);
        setSuccessMessage('Solicitacao aprovada no workflow administrativo.');
      } else {
        await platformSubscriptionChangeRequestsService.reject(decisionModal.request.id, payload);
        setSuccessMessage('Solicitacao rejeitada no workflow administrativo.');
      }
      closeDecision();
      await loadItems();
    } catch (requestError) {
      setError(getApiError(requestError, 'Nao foi possivel processar a solicitacao.'));
    } finally {
      setActionLoading(false);
    }
  };

  const handlePreparePayment = useCallback((requestItem) => {
    if (!requestItem?.id) return;

    Modal.confirm({
      title: 'Preparar pagamento?',
      content:
        'Esta acao cria ou reutiliza um payment intent e uma cobranca local correlacionada. Ela nao ativa a assinatura e nao cobra provider real automaticamente.',
      okText: 'Preparar pagamento',
      cancelText: 'Cancelar',
      okButtonProps: { type: 'primary' },
      onOk: async () => {
        const requestId = requestItem.id;
        setPrepareLoadingById((current) => ({ ...current, [requestId]: true }));
        setError('');
        setSuccessMessage('');

        try {
          const response = await platformSubscriptionChangeRequestsService.preparePayment(requestId);
          const payload = response?.data || null;
          setPrepareSummary(payload);
          setSuccessMessage('Fluxo financeiro preparado com seguranca para a solicitacao aprovada.');
          message.success('Preparacao de pagamento concluida.');
          await loadItems();
        } catch (requestError) {
          const detail = String(requestError?.response?.data?.detail || '').trim();
          if (detail.toUpperCase().includes('APPROVED')) {
            setError('A solicitacao precisa estar APPROVED para preparar pagamento.');
          } else {
            setError(getApiError(requestError, 'Nao foi possivel preparar pagamento com seguranca.'));
          }
        } finally {
          setPrepareLoadingById((current) => {
            const next = { ...current };
            delete next[requestId];
            return next;
          });
        }
      },
    });
  }, [loadItems]);

  const columns = useMemo(() => ([
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      render: (value) => <Text code>{value || '-'}</Text>,
    },
    {
      title: 'Plano atual',
      dataIndex: 'current_plan_code',
      key: 'current_plan_code',
      render: (value) => value || '-',
    },
    {
      title: 'Plano solicitado',
      dataIndex: 'requested_plan_code',
      key: 'requested_plan_code',
      render: (value) => value || '-',
    },
    {
      title: 'Transicao de plano',
      key: 'plan_transition',
      render: (_, row) => <Text>{buildPlanTransitionLabel(row)}</Text>,
    },
    {
      title: 'Tipo',
      dataIndex: 'request_type',
      key: 'request_type',
      render: (value, row) => {
        const normalized = String(value || '').trim().toUpperCase();
        if (normalized === 'DOWNGRADE') {
          return (
            <Space size={4} wrap>
              <Tag color="orange">DOWNGRADE</Tag>
              {isScheduledDowngradeRequest(row) ? <Tag color="blue">Agendado</Tag> : null}
            </Space>
          );
        }
        if (normalized === 'CANCEL') {
          const labels = getScheduledCancelLifecycleLabels(row);
          return (
            <Space size={4} wrap>
              <Tag color="red">CANCEL</Tag>
              {labels.map((label) => {
                const color =
                  label === 'Cancelamento agendado'
                    ? 'blue'
                    : label === 'Cancelamento revogado'
                      ? 'orange'
                      : label === 'Cancelamento processado'
                        ? 'green'
                        : 'default';
                return (
                  <Tag key={`${row?.id || 'cancel'}-${label}`} color={color}>
                    {label}
                  </Tag>
                );
              })}
            </Space>
          );
        }
        return value || '-';
      },
    },
    {
      title: 'Efetivacao agendada',
      key: 'scheduled_effective_on',
      render: (_, row) => {
        if (!isScheduledDowngradeRequest(row) && !isScheduledCancelRequest(row)) return '-';
        const isCancel = String(row?.request_type || '').trim().toUpperCase() === 'CANCEL';
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{`Efetiva em: ${formatDateOnlyPtBR(row?.scheduled_effective_on)}`}</Text>
            <Text type="secondary">{isCancel ? 'Cancelamento ainda nao efetivado' : 'Ainda nao efetivado'}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => <Tag color={STATUS_COLOR[value] || 'default'}>{value || '-'}</Tag>,
    },
    {
      title: 'Fulfillment',
      dataIndex: 'fulfillment',
      key: 'fulfillment',
      render: (value) => renderFulfillmentTag(value),
    },
    {
      title: 'Status financeiro',
      dataIndex: 'payment_state',
      key: 'payment_state',
      render: (value) => renderPaymentStateTag(value),
    },
    {
      title: 'Execucao',
      dataIndex: 'execution_mode',
      key: 'execution_mode',
      render: (value) => renderExecutionMode(value),
    },
    {
      title: 'Correlacao billing',
      dataIndex: 'billing_correlation_id',
      key: 'billing_correlation_id',
      render: (value) => (value ? <Text code>{value}</Text> : '-'),
    },
    {
      title: 'Aviso operacional',
      key: 'operational_warning',
      render: (_, row) => getOperationalWarning(row),
    },
    {
      title: 'Reason code',
      dataIndex: 'reason_code',
      key: 'reason_code',
      render: (value) => value || '-',
    },
    {
      title: 'No-op reason',
      dataIndex: 'noop_reason',
      key: 'noop_reason',
      render: (value) => value || '-',
    },
    {
      title: 'Criado em',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => formatDateTime(value),
    },
    {
      title: 'Solicitante',
      dataIndex: 'requested_by_email',
      key: 'requested_by_email',
      render: (value, row) => value || row.requested_by_user_id || '-',
    },
    {
      title: 'Motivo',
      dataIndex: 'reason',
      key: 'reason',
      render: (value) => value || '-',
    },
    {
      title: 'Acoes',
      key: 'actions',
      render: (_, row) => {
        const canApprove = canExecuteAction('subscription.approve');
        const canReject = canExecuteAction('subscription.reject');
        const canPreparePayment = canExecuteAction('subscription.prepare_payment');

        const isPending = row.status === 'PENDING';
        const canShowPreparePayment = row.status === 'APPROVED' && String(row.request_type || '').trim().toUpperCase() === 'UPGRADE';

        return (
          <Space>
            {withPermissionTooltip(
              <Button
                size="small"
                type="primary"
                disabled={!isPending || !canApprove}
                onClick={() => openDecision('approve', row)}
              >
                Aprovar
              </Button>,
              !canApprove
            )}
            {withPermissionTooltip(
              <Button
                size="small"
                danger
                disabled={!isPending || !canReject}
                onClick={() => openDecision('reject', row)}
              >
                Rejeitar
              </Button>,
              !canReject
            )}
            {canShowPreparePayment
              ? withPermissionTooltip(
                <Button
                  size="small"
                  loading={Boolean(prepareLoadingById[row.id])}
                  disabled={!canPreparePayment}
                  onClick={() => handlePreparePayment(row)}
                >
                  Preparar pagamento
                </Button>,
                !canPreparePayment
              )
              : null}
          </Space>
        );
      },
    },
  ]), [canExecuteAction, openDecision, prepareLoadingById, handlePreparePayment]);

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ marginTop: 0 }}>Solicitacoes de Assinatura</Title>
      <Text type="secondary">Backoffice de analise manual de solicitacoes do portal self-service.</Text>

      <Alert
        type="info"
        showIcon
        style={{ marginTop: 16, marginBottom: 16 }}
        message="A aprovacao ainda nao executa alteracao automatica."
        description="Este fluxo apenas registra decisao administrativa. Assinatura real, provider e entitlements nao sao alterados nesta etapa."
      />

      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message="Status comercial e estado operacional sao distintos"
        description="APPROVED indica decisao administrativa. O estado operacional deve ser lido pelo fulfillment."
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Downgrade agendado (futuro)"
        description="Downgrade agendado ainda nao foi efetivado, nao gera cobranca e o plano atual permanece ativo ate scheduled_effective_on."
      />

      {error ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Falha no backoffice"
          description={error}
        />
      ) : null}

      {successMessage ? (
        <Alert
          type="success"
          showIcon
          style={{ marginBottom: 16 }}
          message={successMessage}
        />
      ) : null}

      {prepareSummary ? (
        <Card size="small" title="Resumo da preparacao de pagamento" style={{ marginBottom: 16 }}>
          {prepareSummary.provider_real_enabled === false ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Provider real desabilitado — fluxo seguro/local."
            />
          ) : null}

          {prepareSummary.provider_real_enabled === true ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Provider real habilitado: confirme ambiente antes de prosseguir."
            />
          ) : null}

          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Request ID">{prepareSummary.request_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="Tenant ID">{prepareSummary.tenant_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="Payment Intent ID">{prepareSummary.payment_intent_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="Charge ID">{prepareSummary.charge_id || '-'}</Descriptions.Item>
            <Descriptions.Item label="Payment state">{prepareSummary.payment_state || '-'}</Descriptions.Item>
            <Descriptions.Item label="Charge status">{prepareSummary.charge_status || '-'}</Descriptions.Item>
            <Descriptions.Item label="Provider key">{prepareSummary.provider_key || '-'}</Descriptions.Item>
            <Descriptions.Item label="Provider real enabled">
              <Tag color={prepareSummary.provider_real_enabled ? 'warning' : 'default'}>
                {prepareSummary.provider_real_enabled ? 'true' : 'false'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Reused payment intent">
              <Tag color={prepareSummary.reused_payment_intent ? 'processing' : 'default'}>
                {prepareSummary.reused_payment_intent ? 'true' : 'false'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Reused charge">
              <Tag color={prepareSummary.reused_charge ? 'processing' : 'default'}>
                {prepareSummary.reused_charge ? 'true' : 'false'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Next action">{prepareSummary.next_action || '-'}</Descriptions.Item>
            <Descriptions.Item label="Warnings">
              {Array.isArray(prepareSummary.warnings) && prepareSummary.warnings.length > 0
                ? prepareSummary.warnings.join(', ')
                : '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ) : null}

      <Card size="small" title="Filtros" style={{ marginBottom: 16 }}>
        <Form layout="vertical" onFinish={applyFilters}>
          <Space wrap style={{ width: '100%' }}>
            <Form.Item name="status" label="Status" style={{ minWidth: 180 }}>
              <Select allowClear options={STATUS_OPTIONS} placeholder="Todos" />
            </Form.Item>
            <Form.Item name="request_type" label="Tipo" style={{ minWidth: 180 }}>
              <Select allowClear options={TYPE_OPTIONS} placeholder="Todos" />
            </Form.Item>
            <Form.Item name="scope" label="Escopo" style={{ minWidth: 260 }}>
              <Select options={SCOPE_OPTIONS} placeholder="Todos" />
            </Form.Item>
            <Form.Item name="tenant_id" label="Tenant ID" style={{ minWidth: 260 }}>
              <Input placeholder="Tenant ID" />
            </Form.Item>
            <Form.Item name="plan_code" label="Plan code" style={{ minWidth: 200 }}>
              <Input placeholder="Ex.: PRO" />
            </Form.Item>
            <Form.Item name="created_range" label="Periodo" style={{ minWidth: 320 }}>
              <DatePicker.RangePicker showTime />
            </Form.Item>
          </Space>

          <div>
            <Space>
              <Button type="primary" htmlType="submit">Aplicar filtros</Button>
              <Button
                onClick={() => {
                  setFilters({});
                  loadItems({});
                }}
              >
                Limpar
              </Button>
            </Space>
          </div>
        </Form>
      </Card>

      <Card size="small" title="Lista de solicitacoes">
        {loading ? (
          <div style={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin size="large" tip="Carregando solicitacoes..." />
          </div>
        ) : (
          <Table
            rowKey={(row) => row.id}
            columns={columns}
            dataSource={items}
            pagination={{ pageSize: 20, showSizeChanger: false }}
          />
        )}
      </Card>

      <Modal
        open={decisionModal.open}
        title={decisionModal.mode === 'approve' ? 'Aprovar solicitacao' : 'Rejeitar solicitacao'}
        onCancel={closeDecision}
        onOk={() => form.submit()}
        okText={decisionModal.mode === 'approve' ? 'Aprovar' : 'Rejeitar'}
        confirmLoading={actionLoading}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Fluxo manual"
          description="Esta decisao nao altera automaticamente a assinatura real."
        />
        <Form form={form} layout="vertical" onFinish={submitDecision}>
          <Form.Item name="internal_notes" label="Observacao interna">
            <Input.TextArea rows={4} placeholder="Detalhes para auditoria interna (opcional)." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default PlatformSubscriptionRequestsPage;
